import { DestroyRef, Injectable, InjectionToken, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';

import { ContextSummary, PageContextV1 } from '@core/models/assistant/assistant.model';
import { PopupWindows } from '@core/services/popup-windows/popup-windows.service';
import { WindowRef } from '@core/services/window-ref.service';

export interface LinkCrumb {
  label: string;
  link: string | null;
}

/** What the linked tab shows, as the child needs it: its page context and its breadcrumb. */
export interface LinkedPage {
  dataSpaceId: number | null;
  pageContext: PageContextV1 | null;
  summary: ContextSummary | null;
  crumbs: LinkCrumb[];
}

export type LinkMessage =
  | { kind: 'context'; page: LinkedPage }
  | { kind: 'hello' }
  | { kind: 'navigate'; url: string }
  | { kind: 'focus' }
  | { kind: 'dock'; activeId: string; draft: string }
  | { kind: 'docked' }
  | { kind: 'bye' }
  | { kind: 'close' };

export interface LinkEnvelope {
  link: string;
  from: 'tab' | 'child';
  /** The tab instance: a tab signs with its own, a child addresses the one that opened it. */
  tab: string;
  message: LinkMessage;
}

export interface LinkChannel {
  post(envelope: LinkEnvelope): void;
  listen(listener: (envelope: LinkEnvelope) => void): void;
  close(): void;
}

export interface TabHooks {
  navigate(url: string): void;
  dock(activeId: string, draft: string): void;
  /** The child never said hello: stopped by a sign-in page, or failed to load. */
  failed(): void;
  /** A child the tab had given up on said hello again and has the chat back. */
  rejoined(): void;
}

export const LINK_CHANNEL = new InjectionToken<LinkChannel | null>('LINK_CHANNEL', {
  providedIn: 'root',
  factory: () => {
    if (typeof BroadcastChannel === 'undefined') return null;
    const channel = new BroadcastChannel('gfe-assistant-link');
    return {
      post: (e) => channel.postMessage(e),
      listen: (l) => (channel.onmessage = (e: MessageEvent<LinkEnvelope>) => l(e.data)),
      close: () => channel.close()
    };
  }
});

const LINK_KEY = 'gfe.assistant.link';
/** Where a tab exposes its instance id on `window`, for the windows it opens to read. */
export const INSTANCE_KEY = 'gfeAssistantTab';
const POPUP_KEY = 'assistant-window';
const OPEN_TIMEOUT_MS = 10_000;
const STALE_MS = 1_000;
const BYE_GRACE_MS = 2_000;
const DOCK_TIMEOUT_MS = 1_000;

type TimerName = 'open' | 'stale' | 'bye' | 'dock';

/**
 * Links a Galia tab to the chat window it popped out, over a BroadcastChannel addressed by link id.
 * There is no heartbeat: Chrome runs a background tab's timers once a minute, so a heartbeat would
 * close the child while the tab merely sits behind another tab. Each side instead watches the
 * other's window where it can, and a reloaded tab finds its child again by the link id kept in its
 * sessionStorage.
 */
@Injectable({ providedIn: 'root' })
export class AssistantWindowLink {
  private readonly channel = inject(LINK_CHANNEL);
  private readonly windowRef = inject(WindowRef);
  private readonly popups = inject(PopupWindows);

  readonly available = this.channel !== null;
  readonly role = signal<'tab' | 'child'>('tab');
  readonly mode = signal<'docked' | 'popped-out'>('docked');
  readonly linked = signal<LinkedPage | null>(null);

  /** Identifies this page load; a duplicated tab copies the link id but not this. */
  private readonly instance = crypto.randomUUID();
  private link: string | null = null;
  private hooks: TabHooks | null = null;
  private page: LinkedPage | null = null;
  private readonly timers = new Map<TimerName, ReturnType<typeof setTimeout>>();
  private childClosed: Subscription | undefined;
  private openerClosed: Subscription | undefined;

  constructor() {
    (this.win as unknown as Record<string, unknown>)[INSTANCE_KEY] = this.instance;
    this.channel?.listen((e) => this.receive(e));
    inject(DestroyRef).onDestroy(() => {
      for (const timer of this.timers.values()) clearTimeout(timer);
      this.childClosed?.unsubscribe();
      this.openerClosed?.unsubscribe();
      this.channel?.close();
    });
  }

  private get win(): Window {
    return this.windowRef.nativeWindow;
  }

  startTab(hooks: TabHooks): void {
    this.hooks = hooks;
    const stored = readLink();
    if (!this.channel || !stored) return;
    this.link = stored;
    this.post({ kind: 'hello' });
    // The id stays in memory: a child that was itself reloading may still answer later.
    this.after('stale', STALE_MS, () => writeLink(null));
  }

  popOut(url: (link: string) => string): 'opened' | 'focused' | 'blocked' {
    const link = this.link ?? crypto.randomUUID();
    const result = this.popups.open(POPUP_KEY, url(link), {
      name: `gfe-assistant-${link}`,
      features: 'width=760,height=900'
    });
    if (result === 'blocked' || (result === 'focused' && this.mode() === 'popped-out'))
      return result;
    // 'focused' while docked: a child the tab gave up on is still open, and gets the chat back.
    this.link = link;
    writeLink(link);
    this.mode.set('popped-out');
    this.after('open', OPEN_TIMEOUT_MS, () => {
      this.endTab(false);
      this.hooks?.failed();
    });
    this.watchChild();
    return result;
  }

  publish(page: LinkedPage): void {
    this.page = page;
    if (this.mode() === 'popped-out') this.post({ kind: 'context', page });
  }

  focusChild(): void {
    if (!this.popups.focus(POPUP_KEY)) this.post({ kind: 'focus' });
  }

  closeChild(): void {
    if (this.mode() !== 'popped-out') return;
    this.post({ kind: 'close' });
    this.endTab(true);
  }

  joinAsChild(link: string): void {
    this.role.set('child');
    this.link = link;
    this.post({ kind: 'hello' });
    this.win.addEventListener('pagehide', () => this.post({ kind: 'bye' }));
    // A missing opener means the tab is gone too: closed, or this window was opened directly.
    this.openerClosed = this.popups.openerClosed().subscribe(() => this.win.close());
  }

  navigate(url: string): void {
    this.post({ kind: 'navigate', url });
  }

  dock(activeId: string, draft: string): void {
    this.post({ kind: 'dock', activeId, draft });
    this.after('dock', DOCK_TIMEOUT_MS, () => this.win.close());
  }

  private receive(envelope: LinkEnvelope): void {
    if (envelope.link !== this.link || envelope.from === this.role()) return;
    // Binds a child to the tab that opened it: a duplicated tab shares the link id, not the instance.
    const tab = this.role() === 'tab' ? this.instance : this.openerInstance();
    if (envelope.tab !== tab) return;
    if (this.role() === 'child') this.receiveAsChild(envelope.message);
    else this.receiveAsTab(envelope.message);
  }

  private receiveAsTab(message: LinkMessage): void {
    switch (message.kind) {
      case 'hello': {
        for (const name of ['open', 'stale', 'bye'] as const) this.clear(name);
        writeLink(this.link);
        const rejoined = this.mode() === 'docked';
        this.mode.set('popped-out');
        this.watchChild();
        if (this.page) this.post({ kind: 'context', page: this.page });
        if (rejoined) this.hooks?.rejoined();
        return;
      }
      case 'bye':
        // A child window this tab still holds is reloading: a real close is seen by PopupWindows.
        if (this.popups.isOpen(POPUP_KEY)) return;
        // Without a reference, a reloading child's hello within the grace keeps the hand-off.
        this.after('bye', BYE_GRACE_MS, () => this.endTab(false));
        return;
      case 'navigate':
        this.hooks?.navigate(message.url);
        this.win.focus();
        return;
      case 'dock':
        this.post({ kind: 'docked' });
        this.endTab(true);
        this.hooks?.dock(message.activeId, message.draft);
        return;
    }
  }

  private receiveAsChild(message: LinkMessage): void {
    switch (message.kind) {
      case 'hello':
        this.post({ kind: 'hello' });
        return;
      case 'context':
        this.linked.set(message.page);
        return;
      case 'focus':
        this.win.focus();
        return;
      case 'docked':
      case 'close':
        this.win.close();
        return;
    }
  }

  /**
   * `forget` drops the link id for good. A timeout keeps it in memory instead: a child that was only
   * slow to load or reload may still say hello, and is then taken back.
   */
  private endTab(forget: boolean): void {
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
    this.childClosed?.unsubscribe();
    this.childClosed = undefined;
    if (forget) this.link = null;
    writeLink(null);
    this.mode.set('docked');
  }

  private post(message: LinkMessage): void {
    if (!this.link) return;
    const tab = this.role() === 'tab' ? this.instance : (this.openerInstance() ?? '');
    this.channel?.post({ link: this.link, from: this.role(), tab, message });
  }

  /** Follows the child window this tab holds, if it holds one: after a reload it holds none. */
  private watchChild(): void {
    this.childClosed?.unsubscribe();
    this.childClosed = this.popups.closed(POPUP_KEY).subscribe(() => this.endTab(true));
  }

  /** The instance id the opener tab exposed; null without a same-origin opener. */
  private openerInstance(): string | null {
    try {
      const id = (this.win.opener as Record<string, unknown> | null)?.[INSTANCE_KEY];
      return typeof id === 'string' ? id : null;
    } catch {
      return null;
    }
  }

  private after(name: TimerName, ms: number, run: () => void): void {
    this.clear(name);
    this.timers.set(
      name,
      setTimeout(() => {
        this.timers.delete(name);
        run();
      }, ms)
    );
  }

  private clear(name: TimerName): void {
    clearTimeout(this.timers.get(name));
    this.timers.delete(name);
  }
}

function readLink(): string | null {
  try {
    return sessionStorage.getItem(LINK_KEY);
  } catch {
    return null;
  }
}

function writeLink(link: string | null): void {
  try {
    if (link) sessionStorage.setItem(LINK_KEY, link);
    else sessionStorage.removeItem(LINK_KEY);
  } catch {
    // Storage unavailable: a reload then forgets the child, which closes once this tab is gone.
  }
}
